/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import get from "lodash/get";
import { LoadMoreStoriesBase, LoadMoreCollectionStories, Collection } from "@quintype/components";
import PropTypes from "prop-types";
import { OneMainCardSlider } from "../layout/one-main-card-slider.js";
import { FullscreenStoryList } from "../layout/fullscreen-story-list.js";
import { ThreeStoryCards } from "../layout/three-story-cards.js";
import { OneStoryCardSixStoryList } from "../layout/onestorycard-sixstorylist";
import { CardGroup } from "../layout/card-group.js";
import { getCollectionTemplate } from "../get-collection-template";
import { updateStoriesWithAlternatives } from "../../utils";
import { overrideLayoutsForSection } from "../../section-page-utils";

const SectionPageBase = props => {
  const bgColor = get(props, ["config", "pagebuilder-config", "section", "pageSetting", "bgColor"], "");
  return (
    <div style={{ backgroundColor: bgColor }}>
      <CollectionLayout {...props} />
    </div>
  );
};

export function CollectionLayout(props) {
  const [customCollection, setCustomCollection] = useState(null);
  const collectionData = get(props, ["data", "collection", "items"], []);

  useEffect(() => {
    // get collection data from slug in pagebuilder config, this request is made from the client side.
    // if there are too many rows with different collection slugs it will take a long time to render on server side.
    const getOverrideLayouts = async () => {
      const setLayout = await overrideLayoutsForSection(props.config, props.data.collection);
      setCustomCollection(setLayout);
    };
    customCollection === null && getOverrideLayouts();
  }, [customCollection]);

  const updatedCollectionWithAlternativesData = updateStoriesWithAlternatives(
    collectionData.filter(item => item.type === "story") // Initially collectionData is null which checks for collection to avoid returning undefined.
  );
  return customCollection && customCollection.items.some(collection => collection.type === "collection") ? (
    <Collection config={props.config} collection={customCollection} collectionTemplates={getCollectionTemplate} />
  ) : (
    <LoadMoreStories
      {...props}
      stories={updatedCollectionWithAlternativesData}
      languageDirection={get(props.config, ["theme-attributes", "language_direction"], "left")}
    />
  );
}
CollectionLayout.propTypes = {
  config: PropTypes.object,
  data: PropTypes.object
};

function getCollectionConfig(collection, section) {
  const name = section ? section["display-name"] || section["name"] : collection.name;
  return {
    name: name,
    "collection-name": name,
    "collection-slug": collection.slug,
    "collection-color": collection.color
  };
}

export function getDefaultTemplate(config, props) {
  const stories = get(props, ["stories"], []);
  return (
    <div>
      <h1 style={{ display: "none" }}>{config["name"]}</h1>
      <CardGroup stories={stories.slice(0, 4)} config={config} />
      <OneMainCardSlider stories={stories.slice(4, 9)} config={config} />
      <ThreeStoryCards stories={stories.slice(9, 12)} config={config} />
      <OneStoryCardSixStoryList stories={stories.slice(12, 19)} config={config} />
      <FullscreenStoryList
        adSlot={true}
        config={config}
        stories={stories.slice(19)}
        onLoadMore={props.onLoadMore}
        noMoreStories={props.noMoreStories}
      />
    </div>
  );
}
getDefaultTemplate.propTypes = {
  stories: PropTypes.array,
  onLoadMore: PropTypes.func,
  noMoreStories: PropTypes.bool
};

function LoadMoreStoriesTemplate(props) {
  const config = getCollectionConfig(props.collection, props.section);
  return getDefaultTemplate(config, props);
}

export function LoadMoreStories(props) {
  const collection = get(props, ["data", "section", "collection"], null);
  const storyFields =
    "headline,subheadline,summary,sections,tags,author-name,author-id,authors,updated-at,last-published-at,published-at,updated-at,first-published-at,hero-image-metadata,hero-image-s3-key,story-content-id,slug,id,seo,story-template,url";
  const data = Object.assign({}, props.data, { stories: get(props, ["stories"], []) });
  const sectionId = get(props, ["data", "section", "id"], -1);
  const numStoriesToLoad = 10;

  return collection ? (
    <LoadMoreCollectionStories
      template={LoadMoreStoriesTemplate}
      collectionSlug={collection.slug}
      params={{ "item-type": "story" }}
      data={data}
      languageDirection={props.languageDirection}
      numStoriesToLoad={numStoriesToLoad}
    />
  ) : (
    <LoadMoreStoriesBase
      template={LoadMoreStoriesTemplate}
      fields={storyFields}
      params={{ "section-id": sectionId }}
      data={data}
      languageDirection={props.languageDirection}
      numStoriesToLoad={numStoriesToLoad}
    />
  );
}
LoadMoreStories.propTypes = {
  data: PropTypes.object,
  languageDirection: PropTypes.string,
  stories: PropTypes.array
};

function mapStateToProps(state) {
  return {
    config: state.qt.config
  };
}

export const SectionPage = connect(mapStateToProps)(SectionPageBase);
